import { useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import { facilityOptionKeys } from '@/lib/queryKeys'

export interface FacilityOption {
  id: string
  name: string
}

const OPTIONS_LIMIT = 200

export async function fetchFacilityOptions(
  token: string
): Promise<FacilityOption[]> {
  const options = new Map<string, FacilityOption>()
  const cursors = new Set<string>()
  let cursor: string | undefined
  do {
    const params = new URLSearchParams({
      limit: String(OPTIONS_LIMIT),
      sort_by: 'facility_name',
      sort_order: 'asc',
    })
    if (cursor) params.set('cursor', cursor)
    const response = await fetch(`${AGENT_URL}/data/facilities?${params}`, {
      headers: { Authorization: token },
    })
    if (!response.ok)
      throw new Error(`Failed to fetch facility options: ${response.status}`)
    const payload = (await response.json()) as {
      data?: { id: string; facility_name: string }[]
      pagination?: { has_more: boolean; next_cursor: string | null }
    }
    if (!Array.isArray(payload.data))
      throw new Error('Invalid facility options response')
    // Relay requires nonblank facility names; skip malformed rows defensively.
    for (const row of payload.data) {
      if (
        !row ||
        typeof row.id !== 'string' ||
        !row.id.trim() ||
        typeof row.facility_name !== 'string' ||
        !row.facility_name.trim()
      ) {
        continue
      }
      options.set(row.id, { id: row.id, name: row.facility_name })
    }
    cursor = payload.pagination?.has_more
      ? (payload.pagination.next_cursor ?? undefined)
      : undefined
    if (payload.pagination?.has_more && (!cursor || cursors.has(cursor))) {
      throw new Error('Incomplete facility options response')
    }
    if (cursor) cursors.add(cursor)
  } while (cursor)
  return [...options.values()]
}

/** The complete org facility directory for filters and booking configuration. */
export function useFacilityOptions(enabled = true) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: facilityOptionKeys.all(organization?.id),
    enabled: enabled && !!organization?.id,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error('No authentication token available')
      return fetchFacilityOptions(token)
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}
